import { Injectable } from '@angular/core';

import { of } from 'rxjs';
import { tap, switchMap, map, catchError } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import * as ProgramSearchResultsActions from '../actions/program-searchresults.action';
import * as fromServices from '../../../scp-common/services';
import * as fromModels from '../../models';
import * as fromReducers from '../reducers';
import { SearchResultsState } from '..';

@Injectable()
export class ProgramSearchResultsEffects {
    constructor(
        private actions$: Actions,
        private ProgramService: fromServices.ProgramService,
        private store$: Store<SearchResultsState>,
    ) { }

    
    processQueryString$ = createEffect(() => this.actions$.pipe(
        ofType(ProgramSearchResultsActions.ProgramSearchResultsActionTypes.ProcessQueryString),
        switchMap((action: ProgramSearchResultsActions.ProcessQueryString) => {
            // Grab an object representation of the query string
            const query = this.parseQueryStringFromUrl(action.payload);
            // Convert query object into our strict-typed object
            let searchCriteria: fromModels.ProgramSearchCriteria;

            // Populate search criteria with the query object params
            searchCriteria = {
                teacherId: query['teacherId'],
                schoolId: query['schoolId'],
                PageNumber: query['PageNumber'],
                PageSize: query['PageSize'],
                SortBy: query['SortBy'],
                SortDir: query['SortDir'],
            };

            return of(new ProgramSearchResultsActions.LoadProgramSearchResults(searchCriteria));
        })
    ));

    
    getResultsForTeacher$ = createEffect(() => this.actions$.pipe(
        ofType(ProgramSearchResultsActions.ProgramSearchResultsActionTypes.GetResultsForTeacher),
        switchMap((action: ProgramSearchResultsActions.GetResultsForTeacher) => {
            console.log(action.payload);

            // Whatever state we have, reset it to initial
            const newCriteria: fromModels.ProgramSearchCriteria = {
                ...fromReducers.initialState.searchCriteria,
                // Use the teacher's district as our criteria
                teacherId: action.payload.id
            };

            return of(new ProgramSearchResultsActions.LoadProgramSearchResults(newCriteria));
        })
    ));

    
    getResultsForSchool$ = createEffect(() => this.actions$.pipe(
        ofType(ProgramSearchResultsActions.ProgramSearchResultsActionTypes.GetResultsForSchool),
        switchMap((action: ProgramSearchResultsActions.GetResultsForSchool) => {
            console.log(action.payload);

            // Whatever state we have, reset it to initial
            const newCriteria: fromModels.ProgramSearchCriteria = {
                ...fromReducers.initialState.searchCriteria,
                // Use the school's district as our criteria
                schoolId: action.payload.id
            };

            return of(new ProgramSearchResultsActions.LoadProgramSearchResults(newCriteria));
        })
    ));

    
    nextPageResult$ = createEffect(() => this.actions$.pipe(
        ofType(ProgramSearchResultsActions.ProgramSearchResultsActionTypes.NextPage),
        switchMap((action: ProgramSearchResultsActions.NextPage) => {
            console.log(action.pagingSorting);
            const currentPage: number = action.pagingSorting.currentPage;
            let nextPage: number;

            // If page number is null, maybe we never got first page?
            if (currentPage == null) {
                // Go to the first one
                nextPage = 1;
            }
            else if (currentPage < action.pagingSorting.totalPages) {
                // Increment to next page
                nextPage = currentPage + 1;
            }
            else {
                // Don't change the page
                nextPage = currentPage;
            }

            // Set the next page to pass to our search request
            const newPayload: fromModels.ProgramSearchCriteria = {
                ...action.payload,
                PageNumber: nextPage
            };
            // TODO: Figure out some way to not do any action here if page number didn't change
            return of(new ProgramSearchResultsActions.LoadProgramSearchResults(newPayload));
        })
    ));

    
    prevPageResult$ = createEffect(() => this.actions$.pipe(
        ofType(ProgramSearchResultsActions.ProgramSearchResultsActionTypes.PrevPage),
        switchMap((action: ProgramSearchResultsActions.PrevPage) => {
            console.log(action.pagingSorting);
            const currentPage: number = action.pagingSorting.currentPage;
            let prevPage: number;

            // If page number is null, maybe we never got first page?
            if (currentPage == null) {
                // Go to the first one
                prevPage = 1;
            }
            else if (currentPage > 1) {
                // Increment to next page
                prevPage = currentPage - 1;
            }
            else {
                // Don't change the page
                prevPage = currentPage;
            }

            // Set the next page to pass to our search request
            const newPayload: fromModels.ProgramSearchCriteria = {
                ...action.payload,
                PageNumber: prevPage
            };
            // TODO: Figure out some way to not do any action here if page number didn't change
            return of(new ProgramSearchResultsActions.LoadProgramSearchResults(newPayload));
        })
    ));

    
    sortByResult$ = createEffect(() => this.actions$.pipe(
        ofType(ProgramSearchResultsActions.ProgramSearchResultsActionTypes.SortBy),
        switchMap((action: ProgramSearchResultsActions.SortBy) => {
            // Default to ascending sort order
            let sortDir = 0;

            // If user is sorting by same column again
            if (action.payload == action.pagingSorting.sortBy) {
                // Change the direction of the sort since it's the same column
                sortDir = action.pagingSorting.sortDir == 0 ? 1 : 0;
            }
            const lastPage: number = action.pagingSorting.totalPages;

            // Set the next page to pass to our search request
            const newPayload: fromModels.ProgramSearchCriteria = {
                ...action.searchCriteria,
                SortDir: sortDir,
                SortBy: action.payload
            };

            return of(new ProgramSearchResultsActions.LoadProgramSearchResults(newPayload));
        })
    ));

    
    lastPageResult$ = createEffect(() => this.actions$.pipe(
        ofType(ProgramSearchResultsActions.ProgramSearchResultsActionTypes.LastPage),
        switchMap((action: ProgramSearchResultsActions.LastPage) => {
            const lastPage: number = action.pagingSorting.totalPages;

            // Set the next page to pass to our search request
            const newPayload: fromModels.ProgramSearchCriteria = {
                ...action.payload,
                PageNumber: lastPage
            };

            return of(new ProgramSearchResultsActions.LoadProgramSearchResults(newPayload));
        })
    ));

    
    firstPageResult$ = createEffect(() => this.actions$.pipe(
        ofType(ProgramSearchResultsActions.ProgramSearchResultsActionTypes.FirstPage),
        switchMap((action: ProgramSearchResultsActions.FirstPage) => {
            const firstPage = 1;

            // Set the next page to pass to our search request
            const newPayload: fromModels.ProgramSearchCriteria = {
                ...action.payload,
                PageNumber: firstPage
            };

            return of(new ProgramSearchResultsActions.LoadProgramSearchResults(newPayload));
        })
    ));

    
    pageSizeResult$ = createEffect(() => this.actions$.pipe(
        ofType(ProgramSearchResultsActions.ProgramSearchResultsActionTypes.SetPageSize),
        switchMap((action: ProgramSearchResultsActions.SetPageSize) => {
            const pageSize: number = action.payload;

            // Set the next page to pass to our search request
            const newPayload: fromModels.ProgramSearchCriteria = {
                ...action.searchCriteria,
                PageSize: pageSize
            };

            return of(new ProgramSearchResultsActions.LoadProgramSearchResults(newPayload));
        })
    ));

    
    searchResults$ = createEffect(() => this.actions$.pipe(
        ofType(ProgramSearchResultsActions.ProgramSearchResultsActionTypes.LoadProgramSearchResults),
        switchMap((action: ProgramSearchResultsActions.LoadProgramSearchResults) => {
            console.log('Load Program Search Results Effect');
            console.log(action.payload);

            let query = '';

            Object.keys(action.payload).forEach(key => {
                const value = action.payload[key];
                if (value === undefined) {
                    // Any missing value we just ignore
                    return;
                }

                const valueAsString = String(value).trim();

                // If item has a value...
                if (value !== null && value !== '') {
                    // If it's one of these keys and value is "Any"
                    const anyKeys = ['state', 'isSupervisory', 'isActive'];
                    if (anyKeys.some(i => i === key) && value == 'Any') {
                        // Treat "any" like null.
                        return;
                    }
                    // Else, add it to query string
                    else {
                        query += `&${key}=${value}`;
                    }
                }
            });

            return this.ProgramService.searchPrograms(query).pipe(
                tap(d => {
                    console.log('searchPrograms returned:', d);
                }),
                map(resp => {
                    if (!resp.ok || resp.body == null) {
                        return new ProgramSearchResultsActions.LoadProgramSearchResultsFailure('Search results not returned');
                    }

                    return new ProgramSearchResultsActions.LoadProgramSearchResultsSuccess(resp.body, resp.headers);
                }),
                catchError(error => of(new ProgramSearchResultsActions.LoadProgramSearchResultsFailure(error)))
            );
        })
    ));

    private parseQueryStringFromUrl(url: string): object {
        const returnObject: object = {
        };

        // Parse query string into chunks
        const splitted = url.split('?', 2);
        // If we have a query string, then process it further
        if (splitted.length == 2) {
            // Split out each key/value pair
            const splittedItems = splitted[1].split('&');
            splittedItems.forEach(item => {
                // Split out the key and value
                const splittedItem = item.split('=');
                const key = splittedItem[0];
                const value = splittedItem.length > 1 ? splittedItem[1] : '';
                // Add is as a key/value pair in our return object.
                returnObject[key] = value;
            });
        }

        return returnObject;
    }
}
